import { ArrowLeft, Crown, Dices, Moon, Swords, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { franchiseToTeam, TEAMS } from "../components/riotball/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* one hall for every weekly crown — the Riotball league champion franchise, the
   Dolo's Night board walker, and the Dolosway table boss, crowned every Sunday */
export default function Champions() {
  const [league, setLeague] = useState([]);
  const [nights, setNights] = useState([]);
  const [nightDuos, setNightDuos] = useState([]);
  const [tables, setTables] = useState([]);
  const [playoffs, setPlayoffs] = useState([]);
  const [flippedTrophy, setFlippedTrophy] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/riotball/champions`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d) setLeague(d.champions || []); })
      .catch(() => {});
    fetch(`${API}/dolosnight/champions`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d) { setNights(d.champions || []); setNightDuos(d.coop_champions || []); } })
      .catch(() => {});
    fetch(`${API}/dolosway/champions`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d) setTables(d.champions || []); })
      .catch(() => {});
    fetch(`${API}/riotball/playoff-champions`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d) setPlayoffs(d.champions || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <main className="champions-page" data-testid="champions-page">
      <section className="champions-hero">
        <Link to="/" className="back-link" data-testid="champions-back-link">
          <ArrowLeft size={16} /> RETURN TO WORLD
        </Link>
        <Reveal immediate>
          <p className="eyebrow">THE MESH REMEMBERS // CROWNED EVERY SUNDAY</p>
          <h1>HALL OF CHAMPIONS.</h1>
          <p>
            Four halls, one pantheon. The AL Riotball league crowns its top franchise (+100 PTS to aligned runners),
            the Night Board crowns its top walker (+50 PTS), the Dolosway table boss takes the whole weekly pot —
            and every playoff bracket run to the chip is carved into the wall forever.
          </p>
        </Reveal>
      </section>

      <div className="champions-grid">
        <section className="hall-of-champions champions-hall" data-testid="champions-league-hall">
          <span className="hall-title">
            <Swords size={12} /> AL RIOTBALL LEAGUE — WEEKLY FRANCHISE CROWNS
          </span>
          <div className="hall-entries">
            {league.length ? (
              league.map((c) => {
                const team = TEAMS[c.team];
                return (
                  <span className="hall-entry" key={c.week} data-testid={`champions-league-${c.week}`} style={team ? { "--team-color": team.color } : undefined}>
                    <em>{c.week}</em>
                    <strong style={team ? { color: team.color } : undefined}>{team ? team.name : String(c.team).toUpperCase()}</strong>
                    <span>{c.best} {c.best === 1 ? "GOAL" : "GOALS"}</span>
                  </span>
                );
              })
            ) : (
              <span className="champions-empty" data-testid="champions-league-empty">NO CROWNS YET — THE FIRST SUNDAY DECIDES</span>
            )}
          </div>
          <p className="hall-note">Aligned runners collect +100 PTS when their franchise takes the crown.</p>
          <Link to="/riotball" className="champions-play-link" data-testid="champions-play-riotball">ENTER THE ARENA →</Link>
        </section>

        <section className="hall-of-champions champions-hall champions-hall-night" data-testid="champions-night-hall">
          <span className="hall-title">
            <Moon size={12} /> NIGHT BOARD — WEEKLY WALKER CROWNS
          </span>
          <div className="hall-entries">
            {nights.length ? (
              nights.map((c) => (
                <span className="hall-entry" key={c.week} data-testid={`champions-night-${c.week}`}>
                  <em>{c.week}</em>
                  <strong>{c.name}</strong>
                  <span>{c.escapes} ESC · BEST {c.best_supplies}</span>
                </span>
              ))
            ) : (
              <span className="champions-empty" data-testid="champions-night-empty">NO WALKERS CROWNED YET — THE NIGHT IS UNCLAIMED</span>
            )}
          </div>
          <p className="hall-note">Escapes outrank hauls. The walker's linked Titan Tag collects +50 PTS.</p>
          {nightDuos.length ? (
            <>
              <span className="hall-title">
                <Users size={12} /> CO-OP DUO CROWNS
              </span>
              <div className="hall-entries" data-testid="champions-night-duos">
                {nightDuos.map((c) => (
                  <span className="hall-entry is-duo" key={c.week} data-testid={`champions-duo-${c.week}`}>
                    <em>{c.week}</em>
                    <strong>{c.name}{c.duo ? ` — ${c.duo}` : ""}</strong>
                    <span>{c.escapes} ESC · COMBINED BEST {c.best_supplies}</span>
                  </span>
                ))}
              </div>
              <p className="hall-note">The top duo's logging tag collects +30 PTS beside the solo walker.</p>
            </>
          ) : null}
          <Link to="/side-games#dolos-night" className="champions-play-link" data-testid="champions-play-night">ENTER THE MAZE →</Link>
        </section>

        <section className="hall-of-champions champions-hall champions-hall-table" data-testid="champions-table-hall">
          <span className="hall-title">
            <Dices size={12} /> DOLOSWAY TABLE — WEEKLY BOARD BOSS CROWNS
          </span>
          <div className="hall-entries">
            {tables.length ? (
              tables.map((c) => (
                <span className="hall-entry" key={c.week} data-testid={`champions-table-${c.week}`}>
                  <em>{c.week}</em>
                  <strong>{c.name}</strong>
                  <span>{c.wins} {c.wins === 1 ? "WIN" : "WINS"} · TOOK {c.pot} PTS</span>
                </span>
              ))
            ) : (
              <span className="champions-empty" data-testid="champions-table-empty">NO BOARD BOSS YET — THE TABLE IS UNCLAIMED</span>
            )}
          </div>
          <p className="hall-note">Wins outrank scrap. The boss's linked Titan Tag takes the whole weekly pot.</p>
          <Link to="/side-games#dolosway" className="champions-play-link" data-testid="champions-play-table">ROLL THE BONES →</Link>
        </section>

        <section className="hall-of-champions champions-hall champions-hall-playoff" data-testid="champions-playoff-hall">
          <span className="hall-title">
            <Crown size={12} /> PLAYOFF CHAMPIONS — BRACKET RUNS TO THE CHIP
          </span>
          {playoffs.length ? (
            <div className="trophy-case" data-testid="trophy-case">
              {[...new Set(playoffs.map((c) => c.team))].map((franchise, i) => {
                const runs = playoffs.filter((c) => c.team === franchise); // newest first
                const team = TEAMS[franchiseToTeam(franchise)];
                const latest = runs[0];
                const isFlipped = flippedTrophy === franchise;
                return (
                  <figure
                    className={`trophy-card ${isFlipped ? "is-flipped" : ""}`}
                    key={`trophy-${franchise}`}
                    data-testid={`trophy-card-${i}`}
                    style={team ? { "--team-color": team.color } : undefined}
                    role="button"
                    tabIndex={0}
                    aria-label={`${team ? team.name : franchise} — ${runs.length} rings. Flip for title runs`}
                    onClick={() => setFlippedTrophy(isFlipped ? null : franchise)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setFlippedTrophy(isFlipped ? null : franchise); } }}
                  >
                    <div className="trophy-card-inner">
                      <div className="trophy-face trophy-front">
                        <img
                          src={`/assets/rings/ring-${franchise}.jpg`}
                          onError={(e) => { e.currentTarget.src = "/assets/riotball-champion-ending.jpg"; }}
                          alt="Riotball Championship Trophy 2355 — two champions hoist the spiked core over the burning city"
                          loading="lazy"
                        />
                        <span className="trophy-card-sheen" aria-hidden="true" />
                        <figcaption>
                          {team?.emblem ? <img className="trophy-card-emblem" src={team.emblem} alt="" loading="lazy" /> : null}
                          <strong style={team ? { color: team.color } : undefined}>{team ? team.name : String(franchise).toUpperCase()}</strong>
                          <span className="trophy-card-rings" data-testid={`trophy-rings-${i}`}>
                            {runs.length} {runs.length === 1 ? "RING" : "RINGS"}
                          </span>
                          <em>{new Date(latest.at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }).toUpperCase()}</em>
                        </figcaption>
                        <span className="trophy-flip-hint" aria-hidden="true">RUNS ▸</span>
                      </div>
                      <div className="trophy-face trophy-back" data-testid={`trophy-back-${i}`}>
                        <span className="trophy-back-title">TITLE RUNS — EVERY FINAL</span>
                        <ul>
                          {runs.map((r, ri) => {
                            const foe = TEAMS[franchiseToTeam(r.finalist)];
                            return (
                              <li key={`run-${ri}`}>
                                <em>{new Date(r.at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }).toUpperCase()}</em>
                                <strong>{r.player_goals}-{r.finalist_goals}</strong>
                                <span>VS {foe ? foe.name : String(r.finalist).toUpperCase()}</span>
                              </li>
                            );
                          })}
                        </ul>
                        <span className="trophy-flip-hint" aria-hidden="true">◂ BACK</span>
                      </div>
                    </div>
                  </figure>
                );
              })}
            </div>
          ) : null}
          <div className="hall-entries">
            {playoffs.length ? (
              playoffs.map((c, i) => {
                const team = TEAMS[franchiseToTeam(c.team)];
                const opp = c.finalist ? TEAMS[franchiseToTeam(c.finalist)] : null;
                return (
                  <span className="hall-entry" key={`${c.at}-${i}`} data-testid={`champions-playoff-${i}`}>
                    <em>{new Date(c.at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }).toUpperCase()}</em>
                    <strong style={team ? { color: team.color } : undefined}>{team ? team.name : String(c.team).toUpperCase()}</strong>
                    <span>
                      {c.player_goals}–{c.finalist_goals}{opp ? ` VS ${opp.name}` : ""}{c.name ? ` · ${c.name}` : ""}
                    </span>
                  </span>
                );
              })
            ) : (
              <span className="champions-empty" data-testid="champions-playoff-empty">NO BRACKET CHAMPIONS YET — RUN THE PLAYOFFS</span>
            )}
          </div>
          <p className="hall-note">Every completed playoff run is carved here — team, final score, and the fallen finalist.</p>
          <Link to="/riotball" className="champions-play-link" data-testid="champions-play-playoff">RUN THE BRACKET →</Link>
        </section>
      </div>

      <section className="champions-note-section">
        <SectionHeading
          eyebrow="HOW THE CROWNS FALL"
          title="Sundays, 18:00 UTC. No exceptions."
          text="The league table locks its leader first, the Night Board five minutes later, the dice table five after that. All three crowns are recorded forever — a tied board falls to the bigger number, then to whoever got there first."
        />
      </section>

      <Footer />
    </main>
  );
}
